import { useState } from "react";
import { ChevronDown, ChevronRight, PackagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney, formatDate, plur } from "@/lib/format";

const fmtQty = (n) => (n == null ? "0" : Number(n).toLocaleString("cs-CZ", { maximumFractionDigits: 3 }));

// Tab „K naskladnění" — doklady jako rozbalovací skupiny (§7). Sklad se nikde
// nevybírá (řídí ho zakázka dokladu). Bez vybraného skladu jsou tlačítka
// Naskladnit skrytá (ne disabled) a v patičce je nápověda.
export function DocQueue({ docs, money, canReceive, busy, onReceive }) {
  const [open, setOpen] = useState({});
  if (!docs.length) return <div className="p-8 text-center text-muted-foreground text-sm" data-testid="queue-empty">Fronta je prázdná — všechno je naskladněné.</div>;
  return (
    <div className="space-y-2.5" data-testid="doc-queue">
      {docs.map((d) => {
        const isOpen = !!open[d.doc_id + (d.warehouse_id || "")];
        const key = d.doc_id + (d.warehouse_id || "");
        return (
          <div key={key} className="border border-border rounded-[16px] bg-card overflow-hidden" data-testid={`queue-doc-${d.doc_id}`}>
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
              data-testid={`queue-doc-toggle-${d.doc_id}`}
              className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-secondary/40 transition-colors">
              <span className="w-7 h-7 rounded-lg border border-border inline-flex items-center justify-center shrink-0 text-muted-foreground">
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold truncate">{d.number || "bez čísla"}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${d.kind === "uctenka" ? "bg-sky-500/15 text-sky-600 dark:text-sky-400" : "bg-brand/15 text-brand"}`}>
                    {d.kind === "uctenka" ? "Účtenka" : "Faktura"}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {d.supplier || "—"} · {formatDate(d.date)}
                  {money && d.total != null && <> · {formatMoney(d.total)}</>}
                </div>
              </div>
              <span className="text-xs font-semibold bg-secondary text-muted-foreground rounded-full px-2.5 py-1 shrink-0">
                {plur(d.lines.length, "položka", "položky", "položek")}
              </span>
            </button>
            {isOpen && (
              <div className="divide-y divide-border border-t border-border">
                {d.lines.map((li) => (
                  <div key={li.line_id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`queue-line-${li.line_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{li.name}</div>
                      {(li.sku || li.ean) && (
                        <div className="text-xs text-muted-foreground truncate">
                          {li.sku ? `SKU ${li.sku}` : ""}{li.sku && li.ean ? " · " : ""}{li.ean ? `EAN ${li.ean}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-xs text-muted-foreground text-right">
                        <div>{fmtQty(li.qty)} {li.unit}</div>
                        {money && li.line_total != null && <div className="font-semibold text-foreground">{formatMoney(li.line_total)}</div>}
                      </div>
                      {canReceive && (
                        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => onReceive(d, li)} data-testid={`receive-line-${li.line_id}`}>
                          <PackagePlus size={14} className="mr-1" /> Naskladnit
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <div className="px-4 py-3 flex items-center justify-between gap-3 bg-secondary/30">
                  <span className="text-xs text-muted-foreground">{canReceive ? "Naskladní všechny řádky dokladu do skladu zakázky." : "Naskladnit půjde, až vlevo vybereš sklad."}</span>
                  {canReceive && (
                    <Button size="sm" className="rounded-full" disabled={busy} onClick={() => onReceive(d, null)} data-testid={`receive-doc-${d.doc_id}`}>
                      <PackagePlus size={14} className="mr-1" /> Naskladnit celý doklad
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
